import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Check, Circle, AlertTriangle, RefreshCw, LogOut } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";

export type BootstrapStage =
  | "authenticating"
  | "preparing"
  | "permissions"
  | "finalizing"
  | "error"
  | "complete";

interface Step {
  id: BootstrapStage;
  labelKey: string;
  defaultLabel: string;
}

const STEPS: Step[] = [
  {
    id: "authenticating",
    labelKey: "bootstrap_step_authenticating",
    defaultLabel: "Authenticating",
  },
  {
    id: "preparing",
    labelKey: "bootstrap_step_preparing",
    defaultLabel: "Preparing workspace",
  },
  {
    id: "permissions",
    labelKey: "bootstrap_step_permissions",
    defaultLabel: "Loading permissions",
  },
  {
    id: "finalizing",
    labelKey: "bootstrap_step_finalizing",
    defaultLabel: "Finalizing",
  },
];

interface AppBootstrapOverlayProps {
  stage: BootstrapStage;
  isComplete: boolean;
  errorMessage?: string;
  onRetry?: () => void;
  onLogout?: () => void;
  variant?: "light" | "dark";
}

// Storage key for registration mode (dark overlay during registration/onboarding)
export const REGISTRATION_MODE_KEY = "guestivo_registration_mode";

/**
 * AppBootstrapOverlay - Full-screen overlay shown during app initialization.
 * Shows a step-based progress indicator for all bootstrap stages.
 * Styled to match HotelSwitchOverlay for visual consistency.
 */
const AppBootstrapOverlay: React.FC<AppBootstrapOverlayProps> = ({
  stage,
  isComplete,
  errorMessage,
  onRetry,
  onLogout,
  variant = "light",
}) => {
  const isDark = variant === "dark";
  const { t } = useTranslation();
  const [isRetrying, setIsRetrying] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [completedSteps, setCompletedSteps] = useState<Set<BootstrapStage>>(
    new Set()
  );
  const [showContent, setShowContent] = useState(false);

  // Delay showing content slightly for smoother initial render
  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Handle visibility - hide immediately when complete (no "all done" flash)
  useEffect(() => {
    if (isComplete) {
      setIsVisible(false);
    }
  }, [isComplete]);

  // Track completed steps based on stage progression
  useEffect(() => {
    if (stage === "complete" || stage === "error") return;

    const stageOrder: BootstrapStage[] = [
      "authenticating",
      "preparing",
      "permissions",
      "finalizing",
    ];
    const currentIndex = stageOrder.indexOf(stage);

    // Mark all previous stages as completed
    if (currentIndex > 0) {
      setCompletedSteps((prev) => {
        const newSet = new Set(prev);
        for (let i = 0; i < currentIndex; i++) {
          newSet.add(stageOrder[i]);
        }
        return newSet;
      });
    }
  }, [stage]);

  // Reset retry state when stage changes
  useEffect(() => {
    if (stage !== "error") {
      setIsRetrying(false);
    }
  }, [stage]);

  const handleRetry = () => {
    setIsRetrying(true);
    onRetry?.();
  };

  const getStepStatus = useMemo(() => {
    return (stepId: BootstrapStage): "completed" | "current" | "pending" => {
      if (completedSteps.has(stepId)) return "completed";
      if (stepId === stage) return "current";
      return "pending";
    };
  }, [completedSteps, stage]);

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        "fixed inset-0 z-100 flex flex-col items-center justify-center",
        "transition-opacity duration-300",
        isComplete ? "opacity-0" : "opacity-100",
        isDark
          ? "bg-linear-to-br from-slate-900 via-slate-800 to-slate-900"
          : "bg-background/95 backdrop-blur-xs"
      )}
    >
      {/* Animated background elements for dark variant (matching OnboardingWizard) */}
      {isDark && (
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-3xl" />
        </div>
      )}
      {/* Content container with animation */}
      <div
        className={cn(
          "flex flex-col items-center text-center",
          "transition-all duration-500",
          showContent ? "scale-100 opacity-100" : "scale-95 opacity-0"
        )}
      >
        {stage === "error" ? (
          <>
            {/* Error icon */}
            <div className="relative mb-6">
              <AlertTriangle className={cn("h-16 w-16", isDark ? "text-red-400" : "text-destructive")} />
            </div>

            {/* Error title */}
            <h1 className={cn("text-2xl font-bold mb-4", isDark ? "text-white" : "text-foreground")}>
              {t("bootstrap_error_title", "Connection Error")}
            </h1>

            {/* Error message */}
            <p className={cn("mb-6 max-w-sm", isDark ? "text-slate-400" : "text-muted-foreground")}>
              {errorMessage || t("bootstrap_error_message", "Unable to connect to the server. Please check your connection and try again.")}
            </p>

            {/* Action buttons */}
            <div className="flex flex-col items-center gap-3">
              {onRetry && (
                <Button
                  onClick={handleRetry}
                  disabled={isRetrying}
                  className="gap-2"
                >
                  <RefreshCw className={cn("h-4 w-4", isRetrying && "animate-spin")} />
                  {isRetrying
                    ? t("bootstrap_retrying", "Retrying...")
                    : t("bootstrap_retry", "Try Again")}
                </Button>
              )}
              {onLogout && (
                <Button
                  variant="ghost"
                  onClick={onLogout}
                  className={cn("gap-2 text-xs", isDark ? "text-slate-400 hover:text-white" : "text-muted-foreground")}
                >
                  <LogOut className="h-3.5 w-3.5" />
                  {t("bootstrap_logout", "Log out and sign in again")}
                </Button>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Building icon with pulse animation */}
            <div className="relative mb-6">
              <Building2 className={cn("h-16 w-16", isDark ? "text-blue-400" : "text-primary")} />
              <div className="absolute inset-0 animate-ping">
                <Building2 className={cn("h-16 w-16", isDark ? "text-blue-400/30" : "text-primary/30")} />
              </div>
            </div>

            {/* Welcome text */}
            <h1 className={cn("text-2xl font-bold mb-10", isDark ? "text-white" : "text-foreground")}>
              {t("bootstrap_welcome", "Welcome to Guestivo")}
            </h1>

            {/* Step indicator */}
            <div className="flex flex-col gap-3 w-64">
              {STEPS.map((step) => {
                const status = getStepStatus(step.id);
                return (
                  <div
                    key={step.id}
                    className={cn(
                      "flex items-center gap-3 transition-all duration-300",
                      status === "completed" && "opacity-60",
                      status === "pending" && "opacity-40"
                    )}
                  >
                    {/* Step indicator icon */}
                    <div
                      className={cn(
                        "flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-all duration-300",
                        status === "completed" && (isDark
                          ? "bg-blue-500 text-white"
                          : "bg-primary text-primary-foreground"),
                        status === "current" && (isDark
                          ? "bg-blue-500/20 text-blue-400 border-2 border-blue-400"
                          : "bg-primary/20 text-primary border-2 border-primary"),
                        status === "pending" && (isDark
                          ? "bg-slate-700 text-slate-500"
                          : "bg-muted text-muted-foreground")
                      )}
                    >
                      {status === "completed" ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : status === "current" ? (
                        <Circle className="h-2.5 w-2.5 fill-current animate-pulse" />
                      ) : (
                        <Circle className="h-2 w-2" />
                      )}
                    </div>

                    {/* Step label */}
                    <span
                      className={cn(
                        "text-sm font-medium transition-all duration-300",
                        status === "completed" && (isDark
                          ? "text-slate-500 line-through"
                          : "text-muted-foreground line-through"),
                        status === "current" && (isDark
                          ? "text-white"
                          : "text-foreground"),
                        status === "pending" && (isDark
                          ? "text-slate-500"
                          : "text-muted-foreground")
                      )}
                    >
                      {t(step.labelKey, step.defaultLabel)}
                      {status === "current" && (
                        <span className="inline-flex ml-1">
                          <span
                            className="animate-bounce"
                            style={{ animationDelay: "0ms" }}
                          >
                            .
                          </span>
                          <span
                            className="animate-bounce"
                            style={{ animationDelay: "150ms" }}
                          >
                            .
                          </span>
                          <span
                            className="animate-bounce"
                            style={{ animationDelay: "300ms" }}
                          >
                            .
                          </span>
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AppBootstrapOverlay;
